import React, { useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import { useFormik } from "formik";
import FormDialog from "../../forms/FormDialog";
import FormSelect from "../../forms/FormSelect";
import { Grid } from "@mui/material";
import FormInput from "../../forms/FormInput";
import CustomTable from "../../components/Tables/CustomTable";
import { warehouseTableKeys } from "../../data/TableData/warehouse";
import URLS from "../../services/urlConstants";
import { del, get, post, put } from "../../services/apiMethods";
import CustomInput from "../../components/SecondaryForms/CustomInput";
import CustomSearch from "../../components/Tables/CustomSearch";

export default function Warehouse() {
  const [isReady, setIsReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [data, setData] = useState([]);
  const [formFields, setFormFields] = useState(null);
  const [loading, setLoading] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [searchFilter, setSearchFilter] = useState([]);
  const [search, setSearch] = useState("");

  const getData = async () => {
    try {
      const { data } = await get(URLS.warehouse.list);
      setData(data.result);
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  };

  const getFormDetails = async () => {
    try {
      const { data } = await get(URLS.forms.read, {
        params: { collectionName: "warehouse" },
      });
      setFormFields(data.result ? data.result.fields : null);
    } catch (error) {
      console.log(error);
    }
  };

  // get data on page load
  useEffect(() => {
    getData();
    getFormDetails();
  }, []);

  const handleOnFieldAdd = () => {
    getData();
    getFormDetails();
  };

  // create || update actions
  const handleCreateOrUpdate = async (values) => {
    try {
      const payload = {
        ...values,
        meta: formFields,
      };

      setLoading(true);
      if (dataToEdit) {
        const { data } = await put(
          URLS.warehouse.update + "/" + dataToEdit._id,
          payload
        );
      } else {
        const { data } = await post(URLS.warehouse.create, payload);
      }

      getData();
      handleModalClose();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues: {
      name: dataToEdit?.name || "",
      address: dataToEdit?.address || "",
      gstIn: dataToEdit?.gstIn || "",
      code: dataToEdit?.code || "",
      description: dataToEdit?.description || "",
    },
    onSubmit: handleCreateOrUpdate,
    enableReinitialize: true,
  });

  // modal actions
  const openDataAddOrUpdateModal = () => setOpen(true);

  const handleModalClose = () => {
    setOpen(false);
    setTimeout(() => {
      setDataToEdit(null);
      setFormFields(
        formFields?.map((f) => ({
          ...f,
          value: "",
        }))
      );
      formik.resetForm();
    }, 200);
  };

  // edit data actions
  const handleCustomInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields(
      formFields?.map((f) => (f.inputLabel === name ? { ...f, value } : f))
    );
  };

  const handleSetDataToEdit = (data) => {
    setReadOnly(false);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );

    setOpen(true);
  };

  const handleDelete = async (id, reason) => {
    try {
      const res = await del(URLS.warehouse.delete + "/" + id, {
        params: {
          reasonForDelete: reason,
        },
      });
      const filtered = data.filter((item) => item._id !== id);
      setData(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  const handleShowData = (data) => {
    setReadOnly(true);
    setDataToEdit(data);
    const fields = data?.meta;
    setFormFields(
      formFields?.map((f) => ({
        ...f,
        value:
          fields?.find((fi) => fi.inputLabel === f.inputLabel)?.value || "",
      }))
    );

    setOpen(true);
  };

  return (
    <>
      <PageHeader
        onAddClick={openDataAddOrUpdateModal}
        collection="Warehouse"
        title="Warehouse"
        modelName="warehouse"
        onFieldAdd={handleOnFieldAdd}
        module="Warehouse"
        showAddField={false}
      />
      <Grid
        container
        spacing={2}
        my={2}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Grid item xs={12} md={6} lg={3}>
          <CustomSearch
            data={data}
            setSearch={setSearch}
            setSearchFilter={setSearchFilter}
            search={search}
            placeholder="Name || Code"
            keys={["name", "code"]}
          />
        </Grid>
      </Grid>
      <CustomTable
        actions={["edit", "view", "delete"]}
        bodyDataModal="warehouse"
        bodyData={search ? searchFilter : data}
        tableKeys={warehouseTableKeys}
        isDataReady={isReady}
        onDeleteClick={handleDelete}
        onEditClick={handleSetDataToEdit}
        onViewClick={handleShowData}
        module="Warehouse"
        formFields={formFields}
      />
      <FormDialog
        readOnly={readOnly}
        open={open}
        adding={loading}
        onClose={handleModalClose}
        submitButtonTitle={dataToEdit ? "Update" : "Add"}
        formik={formik}
        formTitle={dataToEdit ? "Update warehouse" : "Add warehouse"}
      >
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="name"
              type="text"
              formik={formik}
              label="Warehouse Name"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              required={true}
              name="code"
              type="text"
              formik={formik}
              label="Code"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              name="gstIn"
              type="text"
              formik={formik}
              label="GST IN"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              name="description"
              formik={formik}
              label="Description"
              type="text"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormInput
              name="address"
              type="text"
              formik={formik}
              label="Address"
              multiline
              rows={2}
            />
          </Grid>
          {formFields
            ? formFields?.map((f, i) => (
                <Grid key={i} item xs={12} sm={6}>
                  <CustomInput
                    onChange={handleCustomInputChange}
                    name={f.inputLabel}
                    value={f.value}
                    required={f.isRequired}
                    label={f.inputLabel}
                    type={f.inputType}
                  />
                </Grid>
              ))
            : null}
        </Grid>
      </FormDialog>
    </>
  );
}
